import i18next from "i18next"
import { pickBy, identity, mapKeys, filter } from "lodash"
import PostCreateCalendarRequest from "./PostCreateCalendarRequest"
import { messageBuilder } from '../../constants'
import moment from "moment"

/**
 * 
 * @typedef Request
 * @property {String} name
 * @property {Array} channelIds
 * @property {String} communityId
 * @property {String} syncAccountId
 * @property {Boolean} targetEveryone
 */

/**
 * @typedef ValidationError
 * @property {Boolean} isValid
 * @property {Array} messages
 */


class PostUpdateCalendarRequest extends PostCreateCalendarRequest {

    /**
     * Post data ready for usage by the API
     * 
     * @returns {Request}
     */
    getPayload = () => {

        const payload = {
            "name": this.name,
            "sync_account_id": this.syncAccountId,
            "is_sync_automatically": this.syncAutomatically,
            "notify_synced_events": this.sendReminderDayBefore,
            "target_everyone": this.targetEveryone,
            "sync_event_channels": this.syncEventChannels,
            "sync_from_past_date": this.syncFromPastDate,
            "sync_to_future_date": this.syncToFutureDate,
            "sync_from_past_days": this.syncFromPastDays,
            "sync_to_future_days": this.syncToFutureDays,
        }

        const channelIds = filter(this.channelIds, c => c !== messageBuilder.TARGET_ALL_TAG_ID)

        payload["channel_ids"] = channelIds.length > 0 ? channelIds : []

        return payload
    }

    /**
     * Determine error messages from the given data if any
     * 
     * @returns {ValidationError}
     */
    validator = () => {

        const errors = []

        if (!this.name) {
            errors.push({
                field: "name",
                isValid: false,
                message: i18next.t("calendar.accounts.feed.link.requiredName")
            })
        }

        if (this.syncFromPastDays && this.syncFromPastDays < 1) {
            errors.push({
                field: "syncFromPastDays",
                isValid: false,
                message: i18next.t("calendar.accounts.feed.link.requiredSyncFromPastDays")
            })
        }

        if (this.syncFromPastDays && this.syncFromPastDays > 90) {
            errors.push({
                field: "syncFromPastDays",
                isValid: false,
                message: i18next.t("calendar.accounts.feed.link.requiredSyncFromPastDays")
            })
        }

        if (this.syncToFutureDays && this.syncToFutureDays < 1) {
            errors.push({
                field: "syncToFutureDays",
                isValid: false,
                message: i18next.t("calendar.accounts.feed.link.requiredSyncToFutureDays")
            })
        }

        if (this.syncToFutureDays && this.syncToFutureDays > 730) {
            errors.push({
                field: "syncToFutureDays",
                isValid: false,
                message: i18next.t("calendar.accounts.feed.link.requiredSyncToFutureDays")
            })
        }

        // Validate syncFromPastDate is not more than 3 months in the past
        if (this.syncFromPastDate) {
            const threeMonthsAgo = moment().subtract(3, 'months')
            const pastDate = moment(this.syncFromPastDate)
            
            if (pastDate.isBefore(threeMonthsAgo)) {
                errors.push({
                    field: "syncFromPastDate",
                    isValid: false,
                    message: i18next.t("calendar.accounts.feed.link.requiredSyncFromPastDate")
                })
            }
        }

        // Validate syncToFutureDate is not more than 2 years in the future
        if (this.syncToFutureDate) {
            const twoYearsFromNow = moment().add(2, 'years')
            const futureDate = moment(this.syncToFutureDate)
            
            if (futureDate.isAfter(twoYearsFromNow)) {
                errors.push({
                    field: "syncToFutureDate",
                    isValid: false,
                    message: i18next.t("calendar.accounts.feed.link.requiredSyncToFutureDate")
                })
            }
        }

        return {
            isValid: (errors.length === 0),
            messages: mapKeys(errors, error => error.field)
        }
    }

}

export default PostUpdateCalendarRequest